import type { ReactNode } from 'react'

import { columnState, type DescribeReason, type ListColumn, type NotAvailable, type ReasonRule } from './nullState'
import './list.css'

function joinLabels(labels: readonly string[]): string {
  if (labels.length <= 1) return labels.join('')
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`
}

/**
 * Beside the table: the list's own lead sentence, when it has one, and why each shown
 * column the API names is empty: for every row (in the list's own words, "Not live
 * yet"), or for some ("some rows"). An empty list says why it is empty instead, so it
 * has no notes.
 */
export function Notes<Row, Context, Reason extends string>({
  columns,
  rows,
  notAvailable,
  rule,
  describe,
  lead,
}: {
  columns: readonly ListColumn<Row, Context>[]
  rows: readonly Row[]
  notAvailable: NotAvailable<Row, Reason>
  rule: ReasonRule<Row, Reason>
  describe: DescribeReason<Reason>
  lead?: ReactNode
}) {
  if (rows.length === 0) return null
  const byReason = new Map<Reason, { labels: string[]; whole: boolean }>()
  for (const column of columns) {
    const state = columnState(column, rows, notAvailable, rule)
    if (!state) continue
    const entry = byReason.get(state.reason) ?? { labels: [], whole: true }
    entry.labels.push(column.label)
    entry.whole &&= state.whole
    byReason.set(state.reason, entry)
  }
  if (!lead && byReason.size === 0) return null
  return (
    <div className="list-notes">
      {lead ? <p className="list-note">{lead}</p> : null}
      {[...byReason].map(([reason, { labels, whole }]) => (
        <p key={reason} className="list-note" role="note">
          <strong>{whole ? describe(reason).label : 'Not available on some rows'}:</strong> {joinLabels(labels)}. {describe(reason).why}
        </p>
      ))}
    </div>
  )
}
